import { Component, ComponentInterface, h, Host, Prop, State, Watch } from "@stencil/core";

export type OutputVariant = "copytext" | "value";
export type OutputValueAlignment = "left" | "right";

/**
 * @slot flag - Should be used to place a custom flag indicator
 */
@Component({
    tag: "uic-output",
    styleUrl: "output.scss"
})
export class Output implements ComponentInterface {
    /**
     * Indicates the kind of value.
     */
    @Prop() readonly label?: string;

    /**
     * The value.
     */
    @Prop() readonly value?: string;

    /**
     * The unit of the value, e.g "mm", "kg".
     */
    @Prop() readonly unit?: string;

    /**
     * The alignment of the value.
     */
    @Prop({ reflect: true }) readonly align?: OutputValueAlignment = "left";

    /**
     * The output variant. Choose `copytext` to optimize for values with a lot of text.
     */
    @Prop({ reflect: true }) readonly variant: OutputVariant = "value";

    /**
     * If set to `true`, shows a flag that defaults to the typical "error" icon.
     * A string value shows a flag where the name of the flag denotes the icon.
     */
    @Prop({ reflect: true }) readonly flag: boolean | string = false;

    /**
     * If set to `true`, the value can span multiple lines and will not be shortened.
     */
    @Prop() readonly multiline: boolean = false;

    /**
     * Limits number of text lines to show.
     */
    @Prop() readonly maxLines: number = Infinity;

    @State() isMultiline: boolean;

    componentWillLoad() {
        this.trackMultilineState();
    }

    @Watch("multiline")
    @Watch("maxLines")
    trackMultilineState(): void {
        const maxLines = this.maxLines == null ? Infinity : this.maxLines;

        this.isMultiline = (Boolean(this.multiline) && maxLines > 1) || (maxLines > 1 && maxLines < Infinity);
    }

    render() {
        /* eslint-disable-next-line */
        let style: any;

        if (this.maxLines > 1 && this.maxLines < Infinity) {
            style = {
                "-webkit-line-clamp": this.maxLines
            };
        }

        return (
            <Host>
                <span class='uic-output__label'>{this.label}</span>
                <span class='uic-output__content'>
                    <span
                        class={{
                            "uic-output__value": true,
                            "uic-output__value--multiline": this.isMultiline,
                            "uic-output__value--max-lines": this.maxLines > 1 && this.maxLines < Infinity
                        }}
                        style={style}
                    >
                        <slot name='value'>{this.value}</slot>
                        <span class='uic-output__unit'>{this.unit}</span>
                    </span>
                    <span class='uic-output__flag'>
                        <slot name='flag'>
                            <uic-icon
                                name={typeof this.flag === "boolean" || this.flag === "" ? "uic:flag-error" : this.flag}
                            ></uic-icon>
                        </slot>
                    </span>
                </span>
            </Host>
        );
    }
}
